/** Streaks, adherence, weekly averages, every insight, and the micronutrient
 *  report. Micronutrient coverage is shown honestly: a vitamin only a third of
 *  your foods report is marked as partial, not averaged as if it were zero. */

import { useState } from 'react'

import { useModel, useProfile } from '../db/queries'
import { addDays, ageOn, shortDate } from '../lib/days'
import { driFor } from '../lib/dri'
import { adherence, insights, loggingStreak, weeklyAverages, weighInStreak } from '../lib/insights'
import { expenditureOn } from '../lib/model'
import { fmt, fmtKcal, NUTRIENTS } from '../lib/nutrients'

export function StatsScreen() {
  const m = useModel()
  const profile = useProfile()
  const [span, setSpan] = useState(7)
  if (!m || profile === undefined) return <p className="muted">Loading…</p>
  const today = m.today

  const streak = loggingStreak(m.summaries, today)
  const wStreak = weighInStreak(m.weighDays, today)
  const a7 = adherence(m.summaries, today, 7)
  const a28 = adherence(m.summaries, today, 28)
  const weeks = weeklyAverages(m.summaries, today, 8).reverse()
  const all = insights({
    days: m.summaries, today, weighDays: m.weighDays, lastWeighIn: m.lastWeighIn, rate14: m.rate14,
    goalType: profile?.goal_type ?? null, eNow: m.eNow, e28: expenditureOn(m, addDays(today, -28)), sodiumLimit: 2300,
  })

  // Micronutrients over the valid days in the span.
  const days: string[] = []
  for (let i = 1; i <= span; i++) { const d = addDays(today, -i); if (m.summaries.get(d)?.valid) days.push(d) }
  const age = profile?.birth_date ? ageOn(profile.birth_date, today) : 35
  const avgKcal = days.length ? days.reduce((a, d) => a + (m.totals.get(d)?.kcal ?? 0), 0) / days.length : 2000
  const dri = driFor(profile?.sex ?? null, age, avgKcal)
  const micro = dri.map((d) => {
    let s = 0, covered = 0
    for (const day of days) {
      const v = m.totals.get(day)?.nutrients[d.key]
      if (v !== undefined) { s += v; covered++ }
    }
    return { ...d, avg: days.length ? s / days.length : null, coverage: days.length ? covered / days.length : 0 }
  })

  return (
    <div>
      <h1>Stats</h1>
      <div className="grid2">
        <Tile label="Logging streak" value={`${streak}`} sub={streak === 1 ? 'day' : 'days'} />
        <Tile label="Weigh-in streak" value={`${wStreak}`} sub={wStreak === 1 ? 'day' : 'days'} />
        <Tile label="On target, 7 days" value={a7.pct !== null ? `${a7.pct}%` : '—'} sub={`${a7.counted} days counted`} />
        <Tile label="On target, 28 days" value={a28.pct !== null ? `${a28.pct}%` : '—'} sub={`${a28.counted} days counted`} />
      </div>

      <h2>Weekly averages</h2>
      {weeks.length === 0 ? <p className="muted small">Log a few complete days to see weekly averages.</p> : (
        <div className="card" style={{ padding: '.2rem .6rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} className="small num">
            <thead><tr className="muted" style={{ textAlign: 'right' }}>
              <th style={{ textAlign: 'left', padding: '.4rem 0' }}>Week of</th><th>kcal</th><th>Protein</th><th>Days</th></tr></thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week} style={{ borderTop: '1px solid var(--border)', textAlign: 'right' }}>
                  <td style={{ textAlign: 'left', padding: '.45rem 0' }}>{shortDate(w.week)}</td>
                  <td>{fmtKcal(w.kcal)}</td><td>{w.protein_g} g</td><td>{w.days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Insights</h2>
      {all.length === 0 ? <p className="muted small">Insights appear once there's a week or two of logging.</p>
        : all.map((i) => (
          <div key={i.id} className="card" style={{ padding: '.85rem 1rem', marginTop: '.5rem',
            borderLeft: `3px solid ${i.tone === 'good' ? 'var(--accent)' : i.tone === 'warn' ? 'var(--warn)' : 'var(--border)'}` }}>
            <b>{i.title}</b><div className="muted small">{i.body}</div>
          </div>
        ))}

      <h2>Micronutrients</h2>
      <div className="pill-row">
        {[7, 30].map((n) => <button key={n} className={`pill ${span === n ? 'on' : ''}`} onClick={() => setSpan(n)}>{n} days</button>)}
      </div>
      {days.length === 0 ? <p className="muted small">No counted days in this range yet.</p> : (
        <div className="card" style={{ marginTop: '.75rem' }}>
          <p className="muted small" style={{ marginTop: 0 }}>Daily average over {days.length} counted day{days.length === 1 ? '' : 's'}, against the reference intake for your age and sex. Estimates and some foods don't report micronutrients, so partial coverage reads low.</p>
          {micro.map((d) => {
            const pct = d.avg !== null && d.amount ? (d.avg / d.amount) * 100 : 0
            const meta = NUTRIENTS[d.key]
            const good = d.kind === 'limit' ? pct <= 100 : pct >= 90
            return (
              <div key={d.key} style={{ marginTop: '.6rem' }}>
                <div className="row small">
                  <span>{meta.label}{d.kind === 'limit' ? ' (limit)' : ''}</span>
                  <span className="num muted">
                    {d.avg !== null ? fmt(d.avg) : '—'} / {fmt(d.amount)} {meta.unit}
                    {d.coverage < 0.8 ? ` · ${Math.round(d.coverage * 100)}% of days have data` : ''}
                  </span>
                </div>
                <div className="bar-track" style={{ marginTop: '.2rem' }}>
                  <div className="bar-fill" style={{ width: `${Math.min(100, pct)}%`,
                    background: d.coverage < 0.5 ? 'var(--scale-dot)' : good ? 'var(--accent)' : 'var(--warn)' }} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="card" style={{ padding: '1rem' }}>
      <div className="muted small">{label}</div>
      <div className="big num">{value}</div>
      <div className="muted small">{sub}</div>
    </div>
  )
}
